import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { PortfolioSnapshot, Transaction, PriceMap } from '../../types';
import { CardHeader } from '../ui/Card';
import { formatCompactMoney, formatDateShort } from '../../utils/formatters';
import { toDisplaySeries } from '../../utils/displaySeries';
import { todayUtc, useDatedDisplayRates } from '../../hooks/useDatedDisplayRates';
import { useCurrency } from '../../context/CurrencyContext';
import { usePortfolioHistory, PortfolioRange } from '../../hooks/usePortfolioHistory';
import { TrendingUp } from 'lucide-react';
import { CROSSHAIR_STROKE, HorizontalCrosshair } from './HorizontalCrosshair';
import { WalletViewLock } from '../Subscription/WalletViewLock';

interface PortfolioLineChartProps {
  /** USD, like every series below; converted to the display currency here. */
  history: PortfolioSnapshot[];
  transactions: Transaction[];
  prices: PriceMap;
  /**
   * USD the line ends on: the Total Value card's figure, or with a wallet
   * selected, the sum of that wallet's Holdings rows.
   */
  totalValueUsd: number;
  /**
   * Chart one wallet instead of the whole portfolio. `key` is the walletTimeline
   * wallet ("Coinspot", or "Unspecified"); `label` names it in the header. Only
   * passed when the user's tier allows the per-wallet view.
   */
  wallet?: { key: string; label: string } | null;
  /** A wallet the user picked but cannot chart on their tier: shows a Plus lock. */
  lockedWalletLabel?: string | null;
}

const DAY_MS = 86_400_000;

// 90D / 1Y compute accurate historical value; All time is gated (Demo plan tops
// out at 365 days — multi-year history needs a paid CoinGecko tier).
const RANGE_OPTIONS: { id: PortfolioRange; label: string; disabled?: boolean }[] = [
  { id: '90D', label: '90D' },
  { id: '1Y',  label: '1Y' },
  { id: 'ALL', label: 'All time', disabled: true },
];

// Mobile flag (matches Tailwind's `sm` breakpoint). Used only to tune the chart's
// axis density / left gutter on phones — desktop (≥ sm) keeps all current values.
function useIsMobile(): boolean {
  const query = '(max-width: 640px)';
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

export function PortfolioLineChart({
  history, transactions, prices, totalValueUsd, wallet = null, lockedWalletLabel = null,
}: PortfolioLineChartProps) {
  const { fmtDisplay, currency, rate } = useCurrency();
  const isMobile = useIsMobile();

  // Default to 1Y, or 90D when the user has under ~90 days of history.
  const [range, setRange] = useState<PortfolioRange>(() => {
    if (transactions.length === 0) return '1Y';
    const firstMs = transactions.reduce(
      (m, t) => Math.min(m, new Date(t.date).getTime()),
      Infinity
    );
    return isFinite(firstMs) && Date.now() - firstMs < 90 * DAY_MS ? '90D' : '1Y';
  });

  // Switching wallet re-prices the history already fetched; it never refetches.
  const { history: accurate, loading, error } = usePortfolioHistory(transactions, range, prices, wallet?.key ?? null);

  // Prefer the accurate historical series; fall back to the passed-in (synthetic)
  // history while the first fetch is in flight or if it fails entirely.
  // Both are USD. The fallback is whole-portfolio snapshots, so a wallet view
  // never uses it: showing the whole portfolio under a wallet's name would be wrong.
  const series = wallet ? accurate : accurate.length > 0 ? accurate : history;

  // Each past day at that day's rate (see utils/displaySeries).
  const seriesDates = useMemo(() => series.map((p) => p.date), [series]);
  const displayRates = useDatedDisplayRates(seriesDates);

  const data = useMemo(
    () =>
      toDisplaySeries(series, displayRates, {
        date: todayUtc(),
        // Exactly what the Total Value card (or the wallet's rows) shows:
        // fmt(total) = fmtDisplay(total * rate).
        valueDisplay: totalValueUsd * rate,
      }).map((h) => ({
        date: formatDateShort(h.date),
        value: h.value,
      })),
    [series, displayRates, totalValueUsd, rate]
  );

  // Portfolio line is always bronze regardless of direction — direction is
  // already signalled by the All-Time P&L stat card. The single accent reads
  // more "considered" and matches the new design system's framing colour.
  const color = '#d4b483';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-xs text-slate-400 mb-1">{label}</p>
        <p className="text-base font-bold text-slate-100">{fmtDisplay(payload[0].value ?? 0)}</p>
      </div>
    );
  };

  // Segmented range selector (matches the CoinChartPage pill style; theme-aware).
  const rangeSelector = (
    <div className="flex items-center gap-1">
      {RANGE_OPTIONS.map((opt) => {
        const isActive = range === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            disabled={opt.disabled}
            onClick={() => !opt.disabled && setRange(opt.id)}
            title={opt.disabled ? 'All-time history requires multi-year data (coming soon)' : undefined}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-all ${
              opt.disabled
                ? 'text-slate-600 cursor-not-allowed opacity-60'
                : isActive
                ? 'bg-slate-800 text-slate-100 border border-slate-600'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const title = wallet ? `Portfolio Value · ${wallet.label}` : 'Portfolio Value';
  const action = lockedWalletLabel ? (
    <div className="flex items-center gap-2">
      <WalletViewLock walletLabel={lockedWalletLabel} />
      {rangeSelector}
    </div>
  ) : rangeSelector;
  const rangeText = range === '90D' ? 'the last 90 days' : 'the last year';

  // No transactions at all → nothing to chart (selector would be meaningless).
  if (transactions.length === 0 && data.length === 0) {
    return (
      <div>
        <CardHeader title="Portfolio Value" subtitle="Historical performance" />
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <TrendingUp className="w-8 h-8 text-slate-700 mb-3" />
          <p className="text-slate-500 text-sm">No history yet</p>
          <p className="text-slate-600 text-xs mt-1">Chart builds as you add transactions</p>
        </div>
      </div>
    );
  }

  // A wallet that held nothing in the selected range: say so, rather than draw a
  // flat line at zero or wait on a load that has already finished.
  const walletEmpty = !!wallet && !loading && !error &&
    (data.length === 0 || data.every((d) => d.value === 0));
  if (walletEmpty) {
    return (
      <div>
        <CardHeader title={title} subtitle="Historical performance" action={action} />
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <TrendingUp className="w-8 h-8 text-slate-700 mb-3" />
          <p className="text-slate-500 text-sm">{wallet!.label} held nothing in {rangeText}</p>
          <p className="text-slate-600 text-xs mt-1">Try a longer range, or pick another wallet</p>
        </div>
      </div>
    );
  }

  // Have transactions but the accurate series is still loading and there's no
  // fallback data to show — keep the header/selector, show a light placeholder.
  if (data.length === 0) {
    return (
      <div>
        <CardHeader title={title} subtitle="Historical performance" action={action} />
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <p className="text-slate-500 text-sm">{error ?? 'Loading history…'}</p>
        </div>
      </div>
    );
  }

  const minVal = Math.min(...data.map((d) => d.value));
  const maxVal = Math.max(...data.map((d) => d.value));
  const padding = (maxVal - minVal) * 0.1 || maxVal * 0.1;
  // Y-axis band padded around the ACTUAL data range (no zero-clamp) so the line
  // fills the height on long ranges instead of hugging a 0 baseline. The floor is
  // kept just below the lowest value (≥ 2% headroom) so it can never invert.
  const yMin = Math.min(minVal - padding, minVal * 0.98);
  const yMax = maxVal + padding;

  return (
    <div>
      <CardHeader
        title={title}
        subtitle={loading ? 'Updating…' : data.length > 1 ? `${data[0].date} - ${data[data.length - 1].date}` : 'Today'}
        action={action}
      />
      {/* Overflow guard: on small screens the chart can never bleed past its
          column or cause horizontal page scroll (no global overflow-x guard). */}
      <div className="w-full min-w-0 overflow-hidden">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--chart-tick)', fontSize: isMobile ? 10 : 11 }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              // Mobile: widen the min gap so only ~4–5 dates show (no collide/overflow).
              // Desktop: 5 is Recharts' default → identical to before.
              minTickGap={isMobile ? 44 : 5}
            />
            <YAxis
              tick={{ fill: 'var(--chart-tick)', fontSize: isMobile ? 10 : 11 }}
              axisLine={false}
              tickLine={false}
              // Values are already in the display currency (A$40k for AUD).
              tickFormatter={(v: number) => formatCompactMoney(v, currency.code)}
              domain={[yMin, yMax]}
              // Mobile: shrink the fixed left gutter so the plot starts near the edge.
              width={isMobile ? 36 : 56}
            />
            <Tooltip content={renderTooltip} cursor={CROSSHAIR_STROKE} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2.5}
              fill="url(#colorVal)"
              dot={data.length <= 7 ? { fill: color, strokeWidth: 0, r: 4 } : false}
              activeDot={{ fill: color, strokeWidth: 0, r: 5 }}
            />
            <HorizontalCrosshair
              dataKey="value"
              format={(v) => formatCompactMoney(v, currency.code, { precise: true })}
              fontSize={isMobile ? 10 : 11}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
